import { useMemo, useState } from 'react'
import type { FormEvent } from 'react'
import {
  useCreateFeeHead,
  useDeleteFeeHead,
  useFeeHeads,
  useUpdateFeeHead,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal, ConfirmDialog } from '@/components/ui/Modal'
import {
  CheckboxField,
  SelectField,
  TextAreaField,
  TextField,
} from '@/components/ui/Field'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatMoney, formatPercent } from '@/lib/format'
import type { FeeAppliesTo, FeeCalculation, FeeHead } from '@/types/api'
import styles from './Masters.module.css'

/** The amount the preview is calculated against. A round number reads clearly. */
const PREVIEW_BASE = 1000

/**
 * A6 — Fee heads.
 *
 * *** THE FIELDS DO NOT TELL YOU THE TOTAL ***
 * Two 10% fees on a ₹1000 base price produce ₹1200 or ₹1210 depending on whether
 * the second compounds on the first. Nobody works that out from a dropdown
 * labelled "Applies to", and getting it wrong changes what every student is
 * charged.
 *
 * So this screen shows the running total, live, as the admin types. The preview
 * is the feature; the form is how you drive it.
 */
export function FeeHeadsPage() {
  const toast = useToast()
  const { data, isLoading, error, refetch } = useFeeHeads()

  const createMutation = useCreateFeeHead()
  const updateMutation = useUpdateFeeHead()
  const deleteMutation = useDeleteFeeHead()

  const [editing, setEditing] = useState<FeeHead | null>(null)
  const [creating, setCreating] = useState(false)
  const [deleting, setDeleting] = useState<FeeHead | null>(null)

  // Mirrored form state, so the preview updates on every keystroke.
  const [draftValue, setDraftValue] = useState('0')
  const [draftType, setDraftType] = useState<FeeCalculation>('PERCENTAGE')
  const [draftApplies, setDraftApplies] = useState<FeeAppliesTo>('BASE')

  const feeHeads = useMemo(
    () => [...(data?.fee_heads ?? [])].sort((a, b) => a.display_order - b.display_order),
    [data],
  )

  const openCreate = () => {
    setDraftValue('0')
    setDraftType('PERCENTAGE')
    setDraftApplies('BASE')
    setCreating(true)
  }

  const openEdit = (row: FeeHead) => {
    setDraftValue(row.value)
    setDraftType(row.calculation_type)
    setDraftApplies(row.applies_to)
    setEditing(row)
  }

  const closeForm = () => {
    setCreating(false)
    setEditing(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const shared = {
      name: String(form.get('name') ?? '').trim(),
      description: String(form.get('description') ?? '').trim() || null,
      // Sent as a string: the server takes a decimal, and round-tripping
      // through a JS number is exactly the precision loss to avoid.
      value: String(form.get('value') ?? '0').trim(),
      applies_to: draftApplies,
      display_order: Number(form.get('display_order') ?? 0),
    }

    try {
      if (editing) {
        /*
         * *** UPDATE TAKES FEWER FIELDS THAN CREATE ***
         * `calculation_type` and `is_taxable` are absent from FeeHeadUpdate on
         * purpose: a booking snapshots its fees as JSON at purchase time, so
         * changing how an existing fee is calculated would leave live bookings
         * described by a rule that no longer matches what was charged.
         *
         * Sending them anyway is what produced "Extra inputs are not
         * permitted" — the server was right to refuse. To change a fee's
         * calculation type, deactivate it and add a replacement.
         */
        await updateMutation.mutateAsync({
          id: editing.fee_head_id,
          input: shared,
        })
        toast.success(`“${shared.name}” updated.`)
      } else {
        await createMutation.mutateAsync({
          ...shared,
          calculation_type: draftType,
          is_taxable: form.get('is_taxable') === 'on',
        })
        toast.success(`“${shared.name}” added.`)
      }
      closeForm()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save the fee.',
      )
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteMutation.mutateAsync(deleting.fee_head_id)
      toast.success(`“${deleting.name}” removed.`)
      setDeleting(null)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not remove it.',
      )
      setDeleting(null)
    }
  }

  const columns: Array<Column<FeeHead>> = [
    {
      key: 'name',
      header: 'Fee',
      render: (row) => (
        <div>
          <span className={styles.primaryText}>{row.name}</span>
          {row.description && (
            <p className={styles.secondaryText}>{row.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => (
        <span className="numeric">
          {row.calculation_type === 'PERCENTAGE'
            ? formatPercent(row.value)
            : formatMoney(row.value)}
        </span>
      ),
    },
    {
      key: 'applies',
      header: 'Calculated on',
      render: (row) =>
        row.calculation_type === 'FIXED' ? (
          <span className={styles.secondaryText}>Fixed amount</span>
        ) : row.applies_to === 'BASE' ? (
          <Pill tone="neutral">Base price</Pill>
        ) : (
          // Compounding is the surprising one; it gets the accent.
          <Pill tone="warning">Price + earlier fees</Pill>
        ),
    },
    {
      key: 'order',
      header: 'Order',
      align: 'right',
      secondary: true,
      render: (row) => <span className="numeric">{row.display_order}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.is_active ? (
          <Pill tone="success">Active</Pill>
        ) : (
          <Pill tone="neutral">Inactive</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '150px',
      render: (row) => (
        <div className={styles.rowActions}>
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDeleting(row)}>
            Remove
          </Button>
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Fee heads"
        description="Charges added at checkout — tax, platform fee, and so on. Applied to session bookings and subscription plans alike."
        actions={
          <Button variant="primary" onClick={openCreate}>
            Add fee
          </Button>
        }
      />

      <Card flush>
        <Table
          columns={columns}
          rows={feeHeads}
          rowKey={(row) => row.fee_head_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No fees configured. Students pay exactly the listed price."
        />
      </Card>

      {feeHeads.length > 0 && (
        <Card
          title="What a student actually pays"
          description={`Worked through on a ${formatMoney(String(PREVIEW_BASE))} base price — a session or a plan — in the order shown above.`}
        >
          <LivePreview
            fees={feeHeads.filter((fee) => fee.is_active)}
            base={PREVIEW_BASE}
          />
        </Card>
      )}

      <Modal
        open={creating || editing !== null}
        onClose={closeForm}
        title={editing ? 'Edit fee' : 'Add fee'}
        size="lg"
      >
        <form
          onSubmit={handleSubmit}
          className={styles.form}
          key={editing?.fee_head_id ?? 'new'}
        >
          <TextField
            label="Name"
            name="name"
            defaultValue={editing?.name ?? ''}
            placeholder="e.g. GST"
            required
            autoFocus
            maxLength={100}
            hint="Shown to the student on the price breakdown."
          />

          <div className={styles.formRow}>
            <SelectField
              label="Type"
              name="calculation_type"
              value={draftType}
              onChange={(event) =>
                setDraftType(event.target.value as FeeCalculation)
              }
              // Fixed once created: live bookings snapshot their fees, so
              // switching percentage <-> fixed would misdescribe what was
              // already charged. Disabled rather than hidden, so the current
              // type stays visible while editing.
              disabled={editing !== null}
              hint={
                editing
                  ? 'Cannot be changed. Add a replacement fee instead.'
                  : undefined
              }
              options={[
                { value: 'PERCENTAGE', label: 'Percentage' },
                { value: 'FIXED', label: 'Fixed amount' },
              ]}
            />

            <TextField
              label={draftType === 'PERCENTAGE' ? 'Percentage' : 'Amount'}
              name="value"
              type="number"
              min={0}
              step="0.01"
              value={draftValue}
              onChange={(event) => setDraftValue(event.target.value)}
              prefix={draftType === 'FIXED' ? '₹' : undefined}
              required
            />
          </div>

          {draftType === 'PERCENTAGE' && (
            <SelectField
              label="Calculated on"
              name="applies_to"
              value={draftApplies}
              onChange={(event) =>
                setDraftApplies(event.target.value as FeeAppliesTo)
              }
              options={[
                { value: 'BASE', label: 'The base price only' },
                {
                  value: 'BASE_PLUS_PRECEDING',
                  label: 'The base price plus earlier fees (compounds)',
                },
              ]}
              hint="Compounding changes the total. The preview below shows by how much."
            />
          )}

          {/* *** THE POINT OF THIS SCREEN *** */}
          <FeePreview
            base={PREVIEW_BASE}
            existing={feeHeads.filter(
              (fee) => fee.is_active && fee.fee_head_id !== editing?.fee_head_id,
            )}
            draft={{
              name: 'This fee',
              calculation_type: draftType,
              value: draftValue || '0',
              applies_to: draftApplies,
            }}
          />

          <TextAreaField
            label="Description"
            name="description"
            defaultValue={editing?.description ?? ''}
            maxLength={500}
          />

          <div className={styles.formRow}>
            <TextField
              label="Order"
              name="display_order"
              type="number"
              min={0}
              defaultValue={String(editing?.display_order ?? feeHeads.length)}
              hint="Compounding fees apply in this order."
            />

            <CheckboxField
              label="This fee is taxable"
              name="is_taxable"
              defaultChecked={editing?.is_taxable ?? false}
              // Same reason as the type field: the update endpoint does not
              // accept it, because past bookings recorded their own tax
              // treatment at the time of purchase.
              disabled={editing !== null}
              hint={
                editing
                  ? 'Cannot be changed after the fee is created.'
                  : 'Marks it for tax reporting. Does not change the calculation.'
              }
            />
          </div>

          <div className={styles.formActions}>
            <Button variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Save changes' : 'Add fee'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="Remove this fee?"
        message={
          <>
            <strong>{deleting?.name}</strong> will stop being added at checkout.
            Students will pay less from now on. Past bookings keep the price they
            agreed to.
          </>
        }
        confirmLabel="Remove"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleting(null)}
      />
    </PageStack>
  )
}

// --------------------------------------------------------------- preview
interface DraftFee {
  name: string
  calculation_type: FeeCalculation
  value: string
  applies_to: FeeAppliesTo
}

interface ComputedLine {
  name: string
  amount: number
  note: string
  isDraft: boolean
}

/**
 * Compute the breakdown exactly as the server's pricing engine does.
 *
 * *** THIS MIRRORS SERVER LOGIC AND IS ONLY A PREVIEW ***
 * The server is the authority on price and rounds each line with
 * ROUND_HALF_UP. This estimate exists so an admin can SEE the effect of a
 * setting before saving it — it never decides what anyone is charged.
 */
function computeLines(
  base: number,
  fees: DraftFee[],
): { lines: ComputedLine[]; total: number } {
  const lines: ComputedLine[] = []
  let runningTotal = base

  for (const fee of fees) {
    const value = Number(fee.value) || 0
    let amount: number
    let note: string

    if (fee.calculation_type === 'FIXED') {
      amount = value
      note = 'fixed'
    } else if (fee.applies_to === 'BASE') {
      amount = (base * value) / 100
      note = `${formatPercent(fee.value)} of ${formatMoney(String(base))}`
    } else {
      // Compounds on everything so far — the case that surprises people.
      amount = (runningTotal * value) / 100
      note = `${formatPercent(fee.value)} of ${formatMoney(runningTotal.toFixed(2))}`
    }

    // Rounded per line, matching the server rather than rounding once at the end.
    amount = Math.round(amount * 100) / 100
    runningTotal += amount

    lines.push({
      name: fee.name,
      amount,
      note,
      isDraft: fee.name === 'This fee',
    })
  }

  return { lines, total: runningTotal }
}

function FeePreview({
  base,
  existing,
  draft,
}: {
  base: number
  existing: FeeHead[]
  draft: DraftFee
}) {
  const { lines, total } = useMemo(() => {
    const all: DraftFee[] = [
      ...existing.map((fee) => ({
        name: fee.name,
        calculation_type: fee.calculation_type,
        value: fee.value,
        applies_to: fee.applies_to,
      })),
      draft,
    ]
    return computeLines(base, all)
  }, [base, existing, draft])

  return (
    <div className={styles.preview}>
      <p className={styles.previewTitle}>
        On a {formatMoney(String(base))} base price
      </p>

      <div className={styles.previewLine}>
        <span className={styles.previewLabel}>Base price</span>
        <span className={`${styles.previewValue} numeric`}>
          {formatMoney(String(base))}
        </span>
      </div>

      {lines.map((line, index) => (
        <div
          key={`${line.name}-${index}`}
          className={styles.previewLine}
          style={line.isDraft ? { color: 'var(--indigo)' } : undefined}
        >
          <span className={styles.previewLabel}>
            {line.name}
            <span className={styles.previewNote}> · {line.note}</span>
          </span>
          <span className={`${styles.previewValue} numeric`}>
            + {formatMoney(line.amount.toFixed(2))}
          </span>
        </div>
      ))}

      <div className={styles.previewTotal}>
        <span>Student pays</span>
        <span className={`${styles.previewTotalValue} numeric`}>
          {formatMoney(total.toFixed(2))}
        </span>
      </div>

      <p className={styles.previewNote}>
        An estimate for guidance. The server calculates the exact price at
        checkout.
      </p>
    </div>
  )
}

function LivePreview({ fees, base }: { fees: FeeHead[]; base: number }) {
  const drafts: DraftFee[] = fees.map((fee) => ({
    name: fee.name,
    calculation_type: fee.calculation_type,
    value: fee.value,
    applies_to: fee.applies_to,
  }))

  const { lines, total } = computeLines(base, drafts)
  const compounding = fees.some(
    (fee) =>
      fee.calculation_type === 'PERCENTAGE' &&
      fee.applies_to === 'BASE_PLUS_PRECEDING',
  )

  return (
    <>
      <div className={styles.preview}>
        <div className={styles.previewLine}>
          <span className={styles.previewLabel}>Base price</span>
          <span className={`${styles.previewValue} numeric`}>
            {formatMoney(String(base))}
          </span>
        </div>

        {lines.map((line, index) => (
          <div key={`${line.name}-${index}`} className={styles.previewLine}>
            <span className={styles.previewLabel}>
              {line.name}
              <span className={styles.previewNote}> · {line.note}</span>
            </span>
            <span className={`${styles.previewValue} numeric`}>
              + {formatMoney(line.amount.toFixed(2))}
            </span>
          </div>
        ))}

        <div className={styles.previewTotal}>
          <span>Student pays</span>
          <span className={`${styles.previewTotalValue} numeric`}>
            {formatMoney(total.toFixed(2))}
          </span>
        </div>
      </div>

      {compounding && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <Callout tone="info" title="One of these fees compounds">
            <p>
              A fee set to “price + earlier fees” is calculated on the running
              total, so the order above changes the final amount. Reorder the
              fees if that is not what you intend.
            </p>
          </Callout>
        </div>
      )}
    </>
  )
}
